import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { FileStatus } from '~/store/resource/resource.type';
import { useDownload } from './useDownload';
import { TDownloadableResource } from '~/types/common.type';
import { getterDownloadList } from '~/store/resource/resourceGetter';

type TAutoExtract = {
  status: FileStatus;
  key?: string;
  fileName: string;
  url: string;
  updateTime: number;
  totalBytes: number;
};

/**
 * 已回報過的紀錄，同一筆在一個 session 內只上報一次。
 * 送 extract 的時機掛在元件 mount 上，切頁籤／重新渲染都會再跑一次，
 * 不去重會把主程序原本的錯誤量原封不動換成等量的警告。
 */
const reportedExtractKeys = new Set<string>();

/**
 * 本機下載紀錄可能缺 filename（key 對不上時 updateDownloadItem 會建出只有 status 的殘缺紀錄），
 * 這種 payload 送到主程序會讓 extractHandler 直接對 undefined 做 split 而崩潰。
 * 回報 key 是為了回推那筆紀錄當初是用哪種 key 格式寫入的。
 */
export const canExtract = (payload: {
  key?: string;
  filename: string;
  url: string;
  status: FileStatus;
  updateTime: number;
}) => {
  if (payload.filename) return true;

  const dedupeKey = payload.key || payload.url || 'unknown';
  if (!reportedExtractKeys.has(dedupeKey)) {
    reportedExtractKeys.add(dedupeKey);
    Sentry.captureMessage('extract payload missing filename', {
      level: 'warning',
      extra: {
        key: payload.key,
        url: payload.url,
        status: payload.status,
        updateTime: payload.updateTime,
      },
    });
  }
  return false;
};

export const getExtractPayload = (item: TDownloadableResource) => {
  const { resource, progress } = item;
  const { url, fileName } = resource.file;
  const { key, updateTime, totalBytes, status } = progress;

  return {
    key,
    filename: fileName,
    url,
    status,
    updateTime,
    bytes: totalBytes,
    remainingBytes: 0,
    keywords: [],
  };
};

export const useAutoExtractEffect = ({
  status,
  key,
  fileName,
  url,
  updateTime,
  totalBytes,
}: TAutoExtract) => {
  const { extract } = useDownload();
  useEffect(() => {
    if (status === FileStatus.completed) {
      const extractPayload = {
        key,
        filename: fileName,
        url,
        status,
        updateTime,
        bytes: totalBytes,
        remainingBytes: 0,
        keywords: [],
      };
      if (!canExtract(extractPayload)) return;
      extract(extractPayload);
    }
  }, [status]);
};

/**
 * 解壓安裝的觸發原本只掛在資源卡片上（useAutoExtractEffect / ListCard），
 * 下載完成當下若使用者已切換科目或離開該頁，卡片早已 unmount，
 * 就沒有人通知主程序開始解壓，狀態會一直停在 completed；
 * 使用者必須切回該頁讓卡片重新 mount 才會繼續。
 *
 * 本 hook 掛在 Layout（不隨換頁 unmount），直接監看整份下載清單，
 * 讓觸發時機與畫面上顯示什麼完全脫鉤。
 *
 * 首次看到某筆紀錄時只記錄狀態、不觸發：app 啟動時清單裡可能有一批
 * 早已停在 completed 的舊項目，一次全部送出會同時 spawn 多個 7z。
 * 那些舊項目維持原本行為，由卡片 mount 時觸發。
 */
export const useAutoExtractAllEffect = () => {
  const { extract } = useDownload();
  const downloadList = useSelector(getterDownloadList);
  const prevStatusRef = useRef(new Map<string, FileStatus>());

  useEffect(() => {
    downloadList.forEach(
      ({ path, fileFormat, filename, url, status, updateTime, fileSize }) => {
        const key = `${path}.${fileFormat}`;
        const prevStatus = prevStatusRef.current.get(key);
        prevStatusRef.current.set(key, status);

        if (prevStatus === undefined) return;
        if (status !== FileStatus.completed || prevStatus === status) return;

        const extractPayload = {
          key,
          filename,
          url,
          status,
          updateTime,
          bytes: fileSize,
          remainingBytes: 0,
          keywords: [],
        };
        if (!canExtract(extractPayload)) return;
        extract(extractPayload);
      },
    );
  }, [downloadList]);
};
